/**
 * La pantalla completa: login, cabecera, busqueda, resultados y cotizacion.
 *
 * Estado en un objeto y repintado de las secciones que dependen de el. Sin
 * framework y sin reactividad: son tres secciones y una pantalla, y un
 * repintado completo de un `<section>` cuesta menos que la dependencia.
 *
 * La UNICA excepcion es el formulario de busqueda, que se monta una vez y no se
 * vuelve a construir: dentro hay un campo con foco y una lista de
 * autocompletado abierta, y repintarlo en cada tecla las tiraria. Cambiar el
 * idioma si lo remonta --con los valores puestos-- porque entonces cambian
 * todas sus etiquetas.
 *
 * ===========================================================================
 * DOS PANTALLAS, Y LA PRIMERA ES LA PUERTA
 *
 * `arrancar` pregunta `GET /sesion` antes de pintar nada. Si contesta, monta la
 * aplicacion; si contesta 401, monta el login [vista/login.ts]. Ese orden es
 * todo el control de acceso que hace ESTA capa, y no es el que cuenta: lo que
 * de verdad cierra la puerta es que la API no contesta a nadie sin sesion
 * [services/api/src/servidor.ts]. Una pantalla no protege datos; solo evita
 * enseñar una aplicacion que no va a funcionar.
 *
 * El selector de agente de la cabecera DESAPARECIO. Quien esta cotizando ya no
 * se elige: sale de la sesion, en el servidor. Con el se fue `usuario_id` del
 * cuerpo de las peticiones [peticiones.ts] y la posibilidad de mirar el
 * catalogo como otra persona.
 */

import {
  alPerderSesion,
  aprobarAlta,
  buscarTarifas,
  cambiarContrasenaPropia,
  cambiarEstadoPisoMargen,
  cambiarEstadoReglaMargen,
  cambiarEstadoUsuario,
  cerrarSesion,
  cerrarSolicitudRfq,
  crearCotizacion,
  crearPisoMargen,
  crearReglaMargen,
  crearSolicitudRfq,
  crearUsuarioAdmin,
  crearUsuariosEnLote,
  ErrorApi,
  iniciarSesion,
  leerSolicitudRfq,
  listarAltas,
  leerCotizacionGuardada,
  listarClientes,
  listarColaAlias,
  listarColaSheets,
  listarCotizaciones,
  listarDirectorio,
  listarEquipos,
  listarMargenes,
  listarProveedoresRfq,
  listarSolicitudesRfq,
  listarUsuariosAdmin,
  marcarEnviadaRfq,
  rechazarAlta,
  registrarRespuestaRfq,
  resetearContrasenaUsuario,
  sustituirPisoMargen,
  sustituirReglaMargen,
  verificarSheet,
  type AliasPendiente,
  type CampoAmbiguo,
  type Capacidad,
  type CreadoAlta,
  type DetalleSolicitudRfq,
  type EquipoCatalogo,
  type OpcionAmbigua,
  type CotizacionGuardada,
  type Margenes,
  type ProveedorDirectorio,
  type ProveedorRfq,
  type CriterioResuelto,
  type RespuestaBusqueda,
  type RespuestaCotizacion,
  type ResultadoTarifa,
  type ResumenCotizacion,
  type ResumenSolicitudRfq,
  type Sesion,
  type SheetPendiente,
  type SolicitudAlta,
  type Tercero,
  type UsuarioAdmin,
  type UsuarioCreado,
} from './api';
import {
  CARGA_VACIA,
  cuerpoDeCarga,
  esEquipoDeFrio,
  faltanDeCarga,
  sugiereReefer,
  type FormularioCarga,
} from './carga';
import { pintarCarga } from './vista/carga';
import { avisoRecorte, el, vaciar } from './dom';
import {
  crearTraductor,
  IDIOMAS,
  nombreIdioma,
  type Idioma,
  type Traductor,
} from './i18n';
import {
  armarBusqueda,
  armarCotizacion,
  armarSolicitudRfq,
  type FormularioBusqueda,
} from './peticiones';
import { guardarIdioma, idiomaGuardado } from './almacen';
import { recuperarSesion } from './sesion';
import {
  CAMBIO_VACIO,
  enfocarCambio,
  pintarCambioObligado,
  type ValoresCambio,
} from './vista/contrasena';
import { enfocarLogin, pintarLogin, type ValoresLogin } from './vista/login';
import {
  LOTE_INICIAL,
  pintarCredenciales,
  pintarLoteUsuarios,
  type FilaLote,
} from './vista/lote-usuarios';
import {
  ALTA_VACIA,
  MIN_CONTRASENA,
  pintarUsuarios,
  type EstadoAltaUsuario,
} from './vista/usuarios';
import {
  montarBusqueda,
  pintarDesambiguacion,
  type ManejadorBusqueda,
} from './vista/busqueda';
import { pintarMarca } from './vista/marca';
import { pintarGrupos } from './vista/resultados';
import { pintarAyuda as pintarManual } from './vista/ayuda';
import { claveRuta, mapaRuta } from './vista/mapa';
import { vacioInicial, vacioSinResultados } from './vista/vacio';
import {
  pintarCotizacion,
  pintarFormularioCotizacion,
  type EstadoFormularioCotizacion,
} from './vista/cotizacion';
import {
  decisionInicial,
  pintarAlta,
  pintarListaAltas,
  type EstadoDecision,
} from './vista/altas';
import { pintarDetalleCotizacion, pintarListaCotizaciones } from './vista/cotizaciones';
import { pintarMargenes, type EstadoFormMargen } from './vista/margenes';
import { pintarAlternarMenu, pintarMenu } from './vista/menu';
import { pintarDirectorio } from './vista/proveedores';
import { pintarTarifas } from './vista/tarifas';
import { rutaDeZona, zonaDeRuta, zonaInicial, zonasVisibles, type Zona } from './zonas';
import {
  pintarFormularioRfq,
  pintarListaSolicitudes,
  pintarSolicitud,
  type EstadoFormularioRespuesta,
  type EstadoFormularioRfq,
} from './vista/rfq';

/** La fecha de hoy en ISO, sin `toISOString()`: ese devuelve UTC y al oeste de
 *  Greenwich, por la tarde, propone el dia siguiente. */
function hoyIso(): string {
  const d = new Date();
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${String(d.getFullYear())}-${mes}-${dia}`;
}

/**
 * Que hay en pantalla.
 *
 * Ya no son tres botones de la cabecera: son ZONAS, con menu lateral y URL
 * propia [zonas.ts]. Cada una ocupa la pantalla entera porque quien abre la
 * cola de tarifas ya no esta mirando resultados de una busqueda.
 *
 * Lo que estaba buscado NO se tira al cambiar de zona: al volver, la busqueda
 * sigue donde estaba. Es la propiedad que hace que el menu se pueda usar sin
 * miedo, y por eso las secciones se OCULTAN en vez de vaciarse.
 */
interface Estado {
  zona: Zona;
  /** Si el menu lateral esta desplegado. Solo manda en movil: en escritorio lo
   *  fuerza el CSS y este valor da igual. */
  menuAbierto: boolean;
  /**
   * La ruta que alguien escribio y no existe, tal cual.
   *
   * `null` en cuanto se llega a una zona de verdad. Vive en el estado y no en
   * una variable suelta porque se pinta, y lo que se pinta se repinta al
   * cambiar de idioma.
   */
  rutaRota: string | null;
  idioma: Idioma;
  /** Quien entro. No es opcional y no se elige: viene de la cookie de sesion,
   *  resuelta en el servidor [sesion.ts]. */
  sesion: Sesion;
  clientes: Tercero[];
  equipos: EquipoCatalogo[];
  modo: string;
  /**
   * Que es lo que viaja [migracion 0033].
   *
   * Se declara UNA vez, en el buscador, y viaja de ahi a la cotizacion y al
   * RFQ. Dos formularios que declaren la misma carga por separado son dos
   * declaraciones que se contradicen: la del RFQ se PRECARGA de esta y se
   * puede afinar alli, porque un RFQ se manda a veces por una carga distinta
   * de la que se buscaba.
   */
  carga: FormularioCarga;
  faltanCarga: readonly string[];

  cargandoBusqueda: boolean;
  busqueda: RespuestaBusqueda | null;
  desambiguacion: readonly CampoAmbiguo[] | null;
  errorBusqueda: ErrorApi | null;

  tarifa: ResultadoTarifa | null;
  formCotizacion: EstadoFormularioCotizacion;
  faltanCotizacion: readonly string[];
  creando: boolean;
  cotizacion: RespuestaCotizacion | null;
  errorCotizacion: ErrorApi | null;

  // --- RFQ ----------------------------------------------------------------
  // Tres modos dentro de la vista, derivados y no guardados: con `rfqDetalle`
  // se pinta la solicitud, con `rfqForm` el formulario, y sin ninguno la lista.
  rfqForm: EstadoFormularioRfq | null;
  rfqProveedores: readonly ProveedorRfq[];
  rfqCargandoProveedores: boolean;
  rfqCreando: boolean;
  rfqFaltan: readonly string[];
  rfqDetalle: DetalleSolicitudRfq | null;
  rfqTrabajando: boolean;
  rfqRespuesta: EstadoFormularioRespuesta;
  rfqCopiado: string | null;
  rfqCopiaManual: string | null;
  rfqLista: readonly ResumenSolicitudRfq[];
  /** La lista venia recortada. Se guarda al lado de la lista porque es una
   *  propiedad de ESA respuesta, no de la pantalla [dom.ts, avisoRecorte]. */
  rfqListaHayMas: boolean;
  rfqListaTodas: boolean;
  rfqCargandoLista: boolean;
  errorRfq: ErrorApi | null;

  // --- Altas de proveedor ---------------------------------------------------
  // Con `altaAbierta` se pinta la solicitud; sin ella, la cola.
  altasLista: readonly SolicitudAlta[];
  altasHayMas: boolean;
  altasTodas: boolean;
  altasCargando: boolean;
  altaAbierta: SolicitudAlta | null;
  altaDecision: EstadoDecision | null;
  altaCreado: CreadoAlta | null;
  altaTrabajando: boolean;
  errorAltas: ErrorApi | null;

  // --- Cotizaciones emitidas -----------------------------------------------
  // Con `cotizacionAbierta` se pinta el documento; sin ella, la lista.
  cotizacionesLista: readonly ResumenCotizacion[];
  cotizacionesHayMas: boolean;
  /** De la RESPUESTA, no del rol: la API dice si acoto la lista a las propias. */
  cotizacionesSoloMias: boolean;
  cotizacionesCargando: boolean;
  cotizacionAbierta: CotizacionGuardada | null;
  errorCotizaciones: ErrorApi | null;

  // --- Tarifas: las dos colas del catalogo ---------------------------------
  sheets: readonly SheetPendiente[];
  alias: readonly AliasPendiente[];
  tarifasCargando: boolean;
  /** El sheet que se esta firmando. Bloquea los demas botones: dos firmas a la
   *  vez desde la misma pantalla son casi siempre un doble clic. */
  tarifasFirmando: string | null;
  tarifasAviso: string | null;
  errorTarifas: ErrorApi | null;

  // --- Proveedores: directorio y cola de altas -----------------------------
  // Dos trabajos en una zona: quien esta dentro y quien pide entrar.
  proveedoresPestana: 'directorio' | 'altas';
  dirLista: readonly ProveedorDirectorio[];
  dirQ: string;
  dirHayMas: boolean;
  dirCargando: boolean;
  dirAbierto: string | null;
  errorDirectorio: ErrorApi | null;

  // --- Administracion: dos trabajos en una zona ----------------------------
  // Usuarios es de ADMIN; margenes es de SUPERVISOR y de ADMIN. Un SUPERVISOR
  // entra a la zona por los margenes y no ve la pestana de usuarios.
  adminPestana: 'usuarios' | 'margenes';

  margenes: Margenes | null;
  margenesTodas: boolean;
  margenesCargando: boolean;
  margenesTrabajando: boolean;
  margenesForm: EstadoFormMargen | null;
  margenesFaltan: readonly string[];
  margenesAviso: string | null;
  errorMargenes: ErrorApi | null;

  // --- Usuarios. Solo ADMIN ------------------------------------------------
  usuariosLista: readonly UsuarioAdmin[];
  usuariosCargando: boolean;
  usuariosTrabajando: boolean;
  usuariosAlta: EstadoAltaUsuario | null;
  /** El formulario de alta multiple. Con `usuariosCredenciales` puesto se pinta
   *  el resultado y no el formulario. */
  usuariosLote: readonly FilaLote[] | null;
  usuariosLoteFaltan: readonly number[];
  /** Lo que la API acaba de crear, con las contrasenas EN CLARO. Se ven una
   *  vez: no se guardan en ningun sitio y no hay forma de volver a leerlas. */
  usuariosCredenciales: readonly UsuarioCreado[] | null;
  usuariosCopiado: boolean;
  usuariosCopiaManual: boolean;
  usuariosReseteo: { readonly id: string; readonly contrasena: string } | null;
  /** Lo que acaba de pasar, en una linea: "creado", "se cerraron 2 sesiones".
   *  Un cambio sobre una cuenta ajena que no se confirma deja al ADMIN sin
   *  saber si surtio efecto. */
  usuariosAviso: string | null;
  errorUsuarios: ErrorApi | null;
}

const RESPUESTA_VACIA: EstadoFormularioRespuesta = {
  proveedorId: '',
  sinOferta: false,
  nota: '',
};

function montarApp(raiz: HTMLElement, sesion: Sesion): void {
  const estado: Estado = {
    // La zona pedida por la URL, si la hay. Sin ella, la primera que este
    // usuario puede abrir [zonas.ts]. Una URL que no existe se dice; no se cae
    // al buscador en silencio.
    zona: zonaDeRuta(window.location.hash) ?? zonaInicial(sesion.usuario.capacidades),
    menuAbierto: false,
    rutaRota: null,
    idioma: idiomaGuardado(),
    sesion,
    clientes: [],
    equipos: [],
    modo: 'FCL',
    carga: CARGA_VACIA,
    faltanCarga: [],

    cargandoBusqueda: false,
    busqueda: null,
    desambiguacion: null,
    errorBusqueda: null,

    tarifa: null,
    formCotizacion: { unidades: 1, clienteId: null },
    faltanCotizacion: [],
    creando: false,
    cotizacion: null,
    errorCotizacion: null,

    rfqForm: null,
    rfqProveedores: [],
    rfqCargandoProveedores: false,
    rfqCreando: false,
    rfqFaltan: [],
    rfqDetalle: null,
    rfqTrabajando: false,
    rfqRespuesta: RESPUESTA_VACIA,
    rfqCopiado: null,
    rfqCopiaManual: null,
    rfqLista: [],
    rfqListaHayMas: false,
    rfqListaTodas: false,
    rfqCargandoLista: false,
    errorRfq: null,

    altasLista: [],
    altasHayMas: false,
    altasTodas: false,
    altasCargando: false,
    altaAbierta: null,
    altaDecision: null,
    altaCreado: null,
    altaTrabajando: false,
    errorAltas: null,

    cotizacionesLista: [],
    cotizacionesHayMas: false,
    cotizacionesSoloMias: true,
    cotizacionesCargando: false,
    cotizacionAbierta: null,
    errorCotizaciones: null,

    sheets: [],
    alias: [],
    tarifasCargando: false,
    tarifasFirmando: null,
    tarifasAviso: null,
    errorTarifas: null,

    proveedoresPestana: 'directorio',
    dirLista: [],
    dirQ: '',
    dirHayMas: false,
    dirCargando: false,
    dirAbierto: null,
    errorDirectorio: null,

    // La pestana inicial es la que este usuario puede abrir. Un SUPERVISOR que
    // aterrizara en Usuarios veria un 403 nada mas entrar en la zona.
    adminPestana: sesion.usuario.capacidades.includes('usuarios.gestionar')
      ? 'usuarios'
      : 'margenes',

    margenes: null,
    margenesTodas: false,
    margenesCargando: false,
    margenesTrabajando: false,
    margenesForm: null,
    margenesFaltan: [],
    margenesAviso: null,
    errorMargenes: null,

    usuariosLista: [],
    usuariosCargando: false,
    usuariosTrabajando: false,
    usuariosAlta: null,
    usuariosLote: null,
    usuariosLoteFaltan: [],
    usuariosCredenciales: null,
    usuariosCopiado: false,
    usuariosCopiaManual: false,
    usuariosReseteo: null,
    usuariosAviso: null,
    errorUsuarios: null,
  };

  /** Quien esta dentro. Atajo: se lee en muchos sitios y `estado.sesion.usuario`
   *  se lee peor. */
  const yo = (): Sesion['usuario'] => estado.sesion.usuario;

  /**
   * Si quien entro puede hacer algo.
   *
   * La lista llega RESUELTA del servidor [services/api/src/permisos.ts]: esta
   * pantalla no la deduce del rol ni lleva dentro una copia de la tabla de
   * capacidades. Dos tablas se separan en cuanto alguien toca una sola, y el
   * sintoma seria un boton que existe y contesta 403, o una zona que existe y
   * nadie encuentra.
   *
   * Y no es el candado: la API exige la capacidad en cada ruta. Esto solo
   * decide que se dibuja, para no ensenar una puerta que no abre.
   */
  const puedo = (c: Capacidad): boolean => yo().capacidades.includes(c);

  let t: Traductor = crearTraductor(estado.idioma);

  // -------------------------------------------------------------------------
  // El esqueleto
  //
  // Cabecera arriba a todo lo ancho, y debajo dos columnas: el menu y el
  // contenido. Las secciones de cada zona viven todas dentro de `principal` y
  // se OCULTAN al cambiar de zona en vez de destruirse: es lo que hace que la
  // busqueda siga donde estaba al volver del RFQ.
  // -------------------------------------------------------------------------

  const cabecera = el('header', { clase: 'cabecera' });
  const barraMenu = el('div', { clase: 'menu-columna' });
  const principal = el('main', { clase: 'principal' });

  const seccionBusqueda = el('section', { clase: 'panel panel-busqueda' });
  // La declaracion de carga vive fuera del formulario de busqueda porque SI se
  // repinta --los campos cambian con la naturaleza-- y el formulario de
  // busqueda no se repinta nunca: dentro hay un campo con foco y una lista de
  // autocompletado abierta. Ver la cabecera de este archivo.
  const seccionCarga = el('section', { clase: 'panel panel-carga' });
  const seccionResultados = el('div', { clase: 'resultados' });
  const seccionCotizar = el('div', { clase: 'cotizar' });
  const seccionCotizaciones = el('div', { clase: 'cotizaciones-vista' });
  const seccionRfq = el('div', { clase: 'rfq' });
  const seccionTarifas = el('div', { clase: 'tarifas-vista' });
  const seccionProveedores = el('div', { clase: 'proveedores-vista' });
  const seccionAltas = el('div', { clase: 'altas' });
  const seccionAdmin = el('div', { clase: 'admin-pestanas' });
  const seccionUsuarios = el('div', { clase: 'usuarios-vista' });
  const seccionMargenes = el('div', { clase: 'margenes-vista' });
  const seccionAyuda = el('div', { clase: 'ayuda-vista' });

  principal.append(
    seccionBusqueda,
    seccionCarga,
    seccionCotizar,
    seccionResultados,
    seccionCotizaciones,
    seccionRfq,
    seccionTarifas,
    seccionProveedores,
    seccionAltas,
    seccionAdmin,
    seccionUsuarios,
    seccionMargenes,
    seccionAyuda,
  );

  raiz.append(cabecera, el('div', { clase: 'disposicion' }, [barraMenu, principal]));

  let busqueda: ManejadorBusqueda | null = null;

  const formInicial = (): FormularioBusqueda => {
    const previos = busqueda?.valores();
    // La carga se lee del ESTADO y no de lo que devolvio el formulario: vive
    // fuera de el --se repinta sola-- y el formulario solo la lleva de paso.
    // Sin esto, remontar la busqueda al cambiar de idioma congelaria la carga
    // que hubiera en el momento de montarla.
    if (previos !== undefined) return { ...previos, carga: estado.carga };
    return {
      pol: '',
      polPais: null,
      pod: '',
      podPais: null,
      modo: estado.modo,
      equipo: '',
      pesoKg: '',
      volumenM3: '',
      bultos: '',
      fechaEmbarque: hoyIso(),
      carga: estado.carga,
    };
  };

  function montarFormulario(): void {
    const valores = formInicial();
    busqueda = montarBusqueda(t, {
      inicial: valores,
      onBuscar: () => void ejecutarBusqueda(),
      onModo: (modo) => {
        estado.modo = modo;
        // En aereo no hay equipos que pedir: el catalogo contesta una lista
        // vacia y la peticion se paga para nada.
        if (modo !== 'AEREO') void cargarEquipos();
      },
    });
    busqueda.setEquipos(estado.equipos);
    vaciar(seccionBusqueda).append(
      el('h2', { texto: t('busqueda.titulo') }),
      busqueda.nodo,
    );
  }

  // -------------------------------------------------------------------------
  // Cabecera: idioma y usuario
  // -------------------------------------------------------------------------

  function pintarCabecera(): void {
    const selectIdioma = el('select', { clase: 'control control-menor' });
    for (const i of IDIOMAS) {
      selectIdioma.appendChild(
        el('option', { texto: nombreIdioma(i), attrs: { value: i } }),
      );
    }
    selectIdioma.value = estado.idioma;
    selectIdioma.addEventListener('change', () => {
      estado.idioma = selectIdioma.value as Idioma;
      guardarIdioma(estado.idioma);
      t = crearTraductor(estado.idioma);
      document.documentElement.lang = estado.idioma;
      // El idioma cambia todas las etiquetas, incluidas las del formulario y
      // las del menu, y el titulo de la pestana.
      montarFormulario();
      pintarTodo();
    });

    /**
     * Quien esta dentro. Texto, no un desplegable.
     *
     * Aqui habia un selector de agente. Ya no: la identidad sale de la sesion y
     * no se puede cambiar desde la pantalla. Lo que queda es decir quien eres
     * --alguien con dos cuentas tiene que poder saber con cual entro--, su rol
     * cuando no es el corriente, y su `ve_costo`, que es lo que explica por que
     * este agente ve una columna que el de al lado no. Las decisiones son del
     * servidor; esto solo las hace legibles.
     */
    const identidad = el('div', { clase: 'sesion-identidad' }, [
      el('span', { clase: 'sesion-nombre', texto: yo().nombre }),
      el('span', {
        clase: 'badge',
        texto: yo().ve_costo ? t('app.veCosto') : t('app.noVeCosto'),
      }),
      // El rol se ensena siempre que no sea el corriente: con cuatro roles,
      // "por que este no ve la cola de tarifas" se contesta mirando aqui.
      yo().rol === 'AGENTE'
        ? null
        : el('span', {
            clase: 'badge badge-primario',
            texto: t(`usuarios.rol.${yo().rol}`),
          }),
    ]);

    vaciar(cabecera).append(
      // El boton del menu va PRIMERO en el orden del documento, antes de la
      // marca: es lo primero que alcanza el tabulador en movil, que es donde
      // sirve para algo.
      pintarAlternarMenu(t, estado.menuAbierto, () => {
        estado.menuAbierto = !estado.menuAbierto;
        pintarCabecera();
        pintarMenuZonas();
      }),
      ...pintarMarca(t),
      el('div', { clase: 'cabecera-controles' }, [
        identidad,
        el('button', {
          clase: 'boton boton-plano',
          texto: t('app.salir'),
          attrs: { type: 'button' },
          onClick: () => void salir(),
        }),
        el('label', { clase: 'etiqueta-menor', texto: t('app.idioma') }),
        selectIdioma,
      ]),
    );
  }

  // -------------------------------------------------------------------------
  // El menu lateral
  //
  // Las zonas que este usuario puede abrir, con el numero de cosas que esperan
  // en cada cola. El numero es lo unico que convierte "hay una zona de
  // Tarifas" en "hay once documentos esperando tu firma": estas colas no avisan
  // a nadie --no hay envio de correo-- y una pantalla que no dice que tiene
  // cosas dentro no la abre nadie.
  // -------------------------------------------------------------------------

  function pintarMenuZonas(): void {
    vaciar(barraMenu).append(
      // La ruta que no existe, encima del menu y no dentro de la zona: no es un
      // error DE la zona --la zona esta bien, es la que estabas mirando-- es un
      // error de la direccion que se escribio. Se cierra a mano, porque
      // taparlo solo con el siguiente clic dejaria al que escribio mal sin
      // saber nunca que escribio mal.
      ...(estado.rutaRota === null
        ? []
        : [
            el(
              'div',
              { clase: 'aviso aviso-neutro ruta-rota', attrs: { role: 'alert' } },
              [
                el('p', { texto: t('app.rutaRota', { ruta: estado.rutaRota }) }),
                el('p', {
                  clase: 'tenue',
                  texto: t('app.rutaRotaDonde', {
                    zona: t(`zona.${estado.zona}.nombre`),
                  }),
                }),
                el('button', {
                  clase: 'boton boton-plano boton-menor',
                  texto: t('app.rutaRotaCerrar'),
                  attrs: { type: 'button' },
                  onClick: () => {
                    estado.rutaRota = null;
                    // Se devuelve la barra de direcciones a la zona que de verdad
                    // se esta viendo: dejarla mintiendo era la mitad del problema.
                    window.location.replace(
                      `${window.location.pathname}${window.location.search}${rutaDeZona(estado.zona)}`,
                    );
                    pintarMenuZonas();
                  },
                }),
              ],
            ),
          ]),
      pintarMenu({
        t,
        zonas: zonasVisibles(yo().capacidades),
        activa: estado.zona,
        pendientes: {
          proveedores: pendientes(),
          tarifas: estado.sheets.filter((x) => x.apto_para_verificar).length,
        },
        abierto: estado.menuAbierto,
        onIr: (zona) => {
          irA(zona);
        },
      }),
    );
  }

  // -------------------------------------------------------------------------
  // Carga inicial
  // -------------------------------------------------------------------------

  async function cargarEquipos(): Promise<void> {
    try {
      const { equipos } = await listarEquipos(estado.modo);
      estado.equipos = equipos;
      busqueda?.setEquipos(equiposOrdenados());
    } catch (error) {
      mostrarErrorGlobal(error);
    }
  }

  /**
   * Los equipos, con los de frio delante cuando la carga es refrigerada.
   *
   * Es la UNICA deduccion que esta pantalla hace sobre el catalogo a partir de
   * la carga, y la unica que no puede estar mal: una carga refrigerada no viaja
   * en un contenedor seco. NO filtra --los secos siguen en la lista-- solo los
   * ordena. Filtrar escondería el 40NOR, que es un reefer usado como seco y que
   * hay quien elige a proposito [CLAUDE.md, Glosario].
   */
  function equiposOrdenados(): EquipoCatalogo[] {
    if (!sugiereReefer(estado.carga)) return estado.equipos;
    return [...estado.equipos].sort((a, b) => {
      const fa = esEquipoDeFrio(a.codigo) ? 0 : 1;
      const fb = esEquipoDeFrio(b.codigo) ? 0 : 1;
      return fa === fb ? 0 : fa - fb;
    });
  }

  // -------------------------------------------------------------------------
  // Que es lo que viaja
  //
  // Se declara aqui y viaja a la cotizacion y al RFQ. No filtra tarifas: ver la
  // cabecera de carga.ts.
  // -------------------------------------------------------------------------

  function pintarCargaVista(): void {
    const c = vaciar(seccionCarga);
    seccionCarga.hidden = estado.zona !== 'cotizar';
    if (estado.zona !== 'cotizar') return;

    c.appendChild(
      pintarCarga({
        t,
        valores: estado.carga,
        prefijo: 'carga',
        faltan: estado.faltanCarga,
        onCambio: (v) => {
          const cambioNaturaleza = v.naturaleza !== estado.carga.naturaleza;
          estado.carga = v;
          estado.faltanCarga = [];
          if (cambioNaturaleza) {
            // Cambian los campos de debajo, y con REFRIGERADA cambia tambien el
            // orden del desplegable de equipos.
            pintarCargaVista();
            busqueda?.setEquipos(equiposOrdenados());
          }
          // Sin repintar cuando solo se teclea: tiraria el foco del campo.
        },
      }),
    );
  }

  async function cargarCatalogos(): Promise<void> {
    try {
      const clientes = await listarClientes();
      estado.clientes = clientes.terceros;
      pintarCabecera();
      await cargarEquipos();
      // La cola de altas se pide al arrancar aunque no se este mirando: es de
      // donde sale el numero del boton, y sin numero nadie la abre. Solo a
      // quien puede verla: para los demas la ruta contesta 403 y el arranque
      // pintaria un error de permisos por una lista que ni siquiera se pidio.
      if (puedo('proveedores.gestionar')) await cargarAltas();
      // Y lo que necesite la zona en la que se ha aterrizado, que puede no ser
      // el buscador: se puede entrar directo a `#/tarifas`.
      await cargarZona(estado.zona);
    } catch (error) {
      mostrarErrorGlobal(error);
    }
  }

  function mostrarErrorGlobal(error: unknown): void {
    estado.errorBusqueda = error instanceof ErrorApi ? error : null;
    pintarResultados();
  }

  // -------------------------------------------------------------------------
  // Busqueda
  // -------------------------------------------------------------------------

  async function ejecutarBusqueda(): Promise<void> {
    if (busqueda === null) return;

    const armada = armarBusqueda(busqueda.valores());
    busqueda.marcarFaltantes(armada.ok ? [] : armada.faltan);
    if (!armada.ok) {
      // Todo lo que puede faltar es un campo del formulario y ya quedo marcado.
      // Antes tambien podia faltar el agente, que no era un campo y obligaba a
      // un estado aparte; ahora sale de la sesion y no puede faltar.
      estado.errorBusqueda = null;
      pintarResultados();
      return;
    }

    estado.cargandoBusqueda = true;
    estado.errorBusqueda = null;
    estado.desambiguacion = null;
    busqueda.setCargando(true);
    pintarResultados();

    try {
      estado.busqueda = await buscarTarifas(armada.cuerpo);
      // Una busqueda nueva invalida la eleccion anterior: cotizar una tarifa
      // que ya no esta en pantalla es cotizar a ciegas.
      estado.tarifa = null;
      estado.cotizacion = null;
    } catch (error) {
      estado.busqueda = null;
      if (error instanceof ErrorApi && error.campos !== undefined) {
        // [Invariante 6][H51] Se pintan las opciones. No se elige ninguna.
        estado.desambiguacion = error.campos;
      } else {
        estado.errorBusqueda = error instanceof ErrorApi ? error : null;
      }
    } finally {
      estado.cargandoBusqueda = false;
      busqueda.setCargando(false);
      pintarResultados();
      pintarCotizar();
    }
  }

  function pintarResultados(): void {
    const c = vaciar(seccionResultados);

    // Fuera de la vista de busqueda no se pinta --pero tampoco se borra del
    // estado--: al volver, los resultados siguen donde estaban.
    if (estado.zona !== 'cotizar') return;

    if (estado.errorBusqueda !== null) {
      c.appendChild(pintarError(estado.errorBusqueda, t));
      return;
    }

    if (estado.desambiguacion !== null) {
      c.appendChild(
        pintarDesambiguacion(t, estado.desambiguacion, (campo, opcion) => {
          elegirDesambiguacion(campo, opcion);
        }),
      );
      return;
    }

    if (estado.cargandoBusqueda) {
      c.appendChild(el('p', { clase: 'tenue', texto: t('app.cargando') }));
      return;
    }

    const r = estado.busqueda;
    // Todavia no se ha buscado. Antes aqui no se pintaba nada, y una pantalla
    // en blanco es indistinguible de una busqueda que devolvio cero y de una
    // que fallo sin avisar.
    if (r === null) {
      c.appendChild(vacioInicial(t));
      return;
    }

    c.appendChild(
      el('div', { clase: 'resumen-busqueda' }, [
        el('span', {
          texto: t('busqueda.totalResultados', { n: String(r.total_resultados) }),
        }),
        el('span', {
          clase: 'tenue',
          texto: resumenCriterio(r.criterio),
        }),
      ]),
    );

    const esAereo = r.criterio.modo === 'AEREO';

    // Banda: sobre los grupos y a todo el ancho, con los puertos etiquetados.
    // Si falta la coordenada de un puerto no se pinta nada --ni el mapa ni la
    // excusa--: en la busqueda seria una linea de disculpa encima de los
    // resultados. Se dice en la cotizacion, que es donde el mapa importa. Ver
    // vista/mapa.ts.
    const mapa = mapaRuta(r.criterio, 'banda', t);
    if (mapa !== null) c.appendChild(mapa.elemento);

    // Se dice una vez, arriba, y no una por tarjeta: la ausencia de montos es
    // una propiedad de la respuesta entera. Repetirla en cada tarifa la
    // convertiria en ruido y dejaria de leerse.
    if (!r.ve_costo) {
      c.appendChild(
        el('p', {
          clase: 'aviso nota-visibilidad',
          texto: t('busqueda.sinCostoCatalogo'),
        }),
      );
    }

    if (r.total_resultados === 0) {
      // La API dice POR QUE no hay nada. Ver la cabecera de vista/vacio.ts.
      c.appendChild(
        vacioSinResultados(t, estado.idioma, r.criterio, r.diagnostico, () => {
          abrirRfq(r.criterio);
        }),
      );
      return;
    }

    for (const nodo of pintarGrupos(r.grupos, {
      t,
      idioma: estado.idioma,
      // Del response entero, no de cada tarifa: la columna de total existe o no
      // segun quien pregunta. Ver OpcionesResultados en vista/resultados.ts.
      veCosto: r.ve_costo,
      esAereo,
      seleccionado: estado.tarifa?.rate_record_id ?? null,
      // El hilo entre la fila que se esta mirando y su trazo en el mapa. La
      // ruta se saca del criterio y no de la tarifa porque hoy la busqueda
      // tiene una sola --la API filtra por pol_id y pod_id--; ver claveRuta en
      // vista/mapa.ts.
      onResaltar: (tarifa) => {
        mapa?.resaltar(
          tarifa === null ? null : claveRuta(r.criterio.pol, r.criterio.pod),
        );
        // [H82] Y el pre-acarreo de ESA tarifa, que no es del criterio: la
        // ruta la comparten todos los resultados y el tramo terrestre es de
        // una sola. Se apaga con la fila, como el resaltado.
        mapa?.preacarreo(tarifa?.aereo?.ruta.aeropuerto_salida ?? null);
      },
      onCotizar: (tarifa) => {
        estado.tarifa = tarifa;
        estado.cotizacion = null;
        estado.errorCotizacion = null;
        estado.faltanCotizacion = [];
        pintarCotizar();
        pintarResultados();
        // Llamada opcional: `scrollIntoView` es una comodidad, no parte de lo
        // que la pantalla promete, y no existe en todos los entornos donde
        // este codigo corre (jsdom, entre otros).
        seccionCotizar.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      },
    })) {
      c.appendChild(nodo);
    }
  }

  /**
   * La linea que resume que se busco.
   *
   * Las dos experiencias resumen cosas distintas porque preguntan cosas
   * distintas: en maritimo el tercer dato es el equipo y en aereo es la carga,
   * que es lo que ahi hace de filtro. Poner "20GP" en un resumen aereo seria
   * afirmar un equipo que la busqueda no mando, y dejar el hueco haria pensar
   * que se perdio un dato [H59].
   */
  function resumenCriterio(criterio: CriterioResuelto): string {
    const ruta =
      `${criterio.pol.nombre} (${criterio.pol.pais})` +
      ` → ${criterio.pod.nombre} (${criterio.pod.pais})`;
    if (criterio.carga !== undefined) {
      const bultos =
        criterio.carga.bultos === null
          ? ''
          : ` · ${t('busqueda.nBultos', { n: criterio.carga.bultos })}`;
      return (
        `${ruta} · ${criterio.carga.peso_kg} ${t('aereo.kg')}` +
        ` · ${criterio.carga.volumen_m3} ${t('busqueda.m3')}${bultos}`
      );
    }
    return `${ruta} · ${criterio.equipo?.codigo ?? ''}`;
  }

  function elegirDesambiguacion(
    campo: 'pol' | 'pod' | 'equipo',
    opcion: OpcionAmbigua,
  ): void {
    busqueda?.aplicarOpcion(campo, opcion);
    // Se limpia la que se resolvio y se dejan las demas: si el POL y el POD
    // eran ambiguos los dos, elegir uno no resuelve el otro.
    const resto = (estado.desambiguacion ?? []).filter((c) => c.campo !== campo);
    estado.desambiguacion = resto.length === 0 ? null : resto;
    if (estado.desambiguacion === null) void ejecutarBusqueda();
    else pintarResultados();
  }

  // -------------------------------------------------------------------------
  // Cotizacion
  // -------------------------------------------------------------------------

  function pintarCotizar(): void {
    const c = vaciar(seccionCotizar);
    if (estado.zona !== 'cotizar') return;

    // El criterio de la busqueda que llevo a esta cotizacion: es de donde sale
    // la ruta del mapa. Si no hay busqueda en pantalla no hay tarifa elegida ni
    // cotizacion recien creada, asi que las dos ramas de abajo no se alcanzan.
    const criterio = estado.busqueda?.criterio ?? null;

    if (estado.cotizacion !== null) {
      c.appendChild(
        pintarCotizacion(estado.cotizacion, t, estado.idioma, criterio, () => {
          estado.cotizacion = null;
          estado.tarifa = null;
          pintarCotizar();
          pintarResultados();
        }),
      );
      return;
    }

    if (estado.errorCotizacion !== null) {
      c.appendChild(pintarError(estado.errorCotizacion, t));
    }

    if (estado.tarifa === null) return;

    c.appendChild(
      pintarFormularioCotizacion({
        t,
        idioma: estado.idioma,
        tarifa: estado.tarifa,
        clientes: estado.clientes,
        valores: estado.formCotizacion,
        creando: estado.creando,
        faltan: estado.faltanCotizacion,
        onCambio: (v) => {
          estado.formCotizacion = v;
        },
        onCrear: () => void ejecutarCotizacion(),
        onCancelar: () => {
          estado.tarifa = null;
          estado.errorCotizacion = null;
          pintarCotizar();
          pintarResultados();
        },
      }),
    );
  }

  async function ejecutarCotizacion(): Promise<void> {
    if (estado.tarifa === null) return;

    // La declaracion se comprueba ANTES de armar: es la misma que ya esta en
    // pantalla arriba, y un 400 del servidor por un campo que se ve desde aqui
    // es un viaje de ida y vuelta para nada.
    const faltanCarga = faltanDeCarga(estado.carga);
    if (faltanCarga.length > 0) {
      estado.faltanCarga = faltanCarga;
      estado.errorCotizacion = null;
      pintarCargaVista();
      pintarCotizar();
      // Se lleva la vista al bloque de carga: el boton que se pulso esta abajo
      // del todo y lo que falta esta arriba, asi que sin esto parece que no
      // paso nada.
      seccionCarga.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      return;
    }
    estado.faltanCarga = [];

    // LA COTIZACION AEREA HEREDA LA ANATOMIA DE LA BUSQUEDA: por kilo, no por
    // contenedor. El peso y el volumen son los MISMOS con los que se busco --de
    // `criterio.carga`, no de los campos del formulario-- porque el escalon y
    // el precio por kilo que el agente vio en pantalla salieron de esas dos
    // cifras. Recogerlas otra vez del formulario dejaria que un peso tecleado
    // despues de buscar produjera una cotizacion con otro escalon y el mismo
    // aspecto.
    const cargaAerea = estado.busqueda?.criterio.carga;

    const armada = armarCotizacion({
      rateRecordId: estado.tarifa.rate_record_id,
      // El equipo del resultado, no el del formulario: es el que la API uso
      // para calcular ese total y esos dias libres. Nulo en aereo, donde no hay
      // ninguno.
      equipo: estado.tarifa.dias_libres.equipo,
      aerea: estado.tarifa.aereo === undefined ? null : (cargaAerea ?? null),
      unidades: estado.formCotizacion.unidades,
      clienteId: estado.formCotizacion.clienteId,
      idioma: estado.idioma,
      // Lo declarado arriba, y SOLO lo que corresponde a su naturaleza: un dato
      // de la naturaleza anterior acabaria en el documento [carga.ts].
      carga: cuerpoDeCarga(estado.carga),
    });

    if (!armada.ok) {
      estado.faltanCotizacion = armada.faltan;
      pintarCotizar();
      return;
    }

    estado.faltanCotizacion = [];
    estado.creando = true;
    estado.errorCotizacion = null;
    pintarCotizar();

    try {
      estado.cotizacion = await crearCotizacion(armada.cuerpo);
    } catch (error) {
      estado.errorCotizacion = error instanceof ErrorApi ? error : null;
    } finally {
      estado.creando = false;
      pintarCotizar();
    }
  }

  // -------------------------------------------------------------------------
  // RFQ: pedir cotizacion a proveedores
  //
  // Aqui no se redacta ningun correo. El texto llega hecho de la API --en el
  // idioma de cada proveedor, no en el de esta pantalla-- y lo unico que se
  // hace con el es pintarlo y copiarlo. Ver la cabecera de vista/rfq.ts.
  // -------------------------------------------------------------------------

  /**
   * Cambiar de zona.
   *
   * Tres efectos, y los tres importan:
   *
   *   1. La URL. `location.hash` pasa a la ruta de la zona, asi que recargar
   *      vuelve aqui, el boton de atras del navegador vuelve a la anterior y
   *      el enlace se puede pegar en un chat [zonas.ts].
   *
   *   2. El titulo de la pestana. Con seis zonas abiertas en seis pestanas,
   *      "Freight Bridge" repetido seis veces no distingue ninguna.
   *
   *   3. El menu se pliega, en movil. Dejarlo abierto encima de lo que se
   *      acaba de pedir es la forma de que el primer clic no parezca haber
   *      hecho nada.
   *
   * `desdeUrl` distingue el clic de la vuelta atras del navegador: en el
   * segundo la URL ya es la buena y volver a escribirla anadiria una entrada al
   * historial, con lo que el boton de atras dejaria de salir de la zona.
   */
  function irA(zona: Zona, desdeUrl = false): void {
    estado.zona = zona;
    estado.menuAbierto = false;
    const ruta = rutaDeZona(zona);
    if (!desdeUrl && window.location.hash !== ruta) window.location.hash = ruta;
    document.title = `${t(`zona.${zona}.nombre`)} · ${t('app.nombre')}`;
    pintarTodo();
    void cargarZona(zona);
  }

  /**
   * Lo que hay que pedir al servidor al entrar en una zona.
   *
   * Se pide AL ENTRAR y no al arrancar: cargar las seis zonas de golpe seria
   * seis peticiones para ver una. La excepcion es la cola de altas, que se pide
   * al arrancar porque de ahi sale el contador del menu, y un contador que solo
   * aparece despues de abrir la zona no sirve para nada.
   *
   * Las que ya tienen datos no se vuelven a pedir: volver del detalle a la
   * lista no deberia parpadear. Refrescar es explicito --el boton de cada
   * zona-- o consecuencia de haber cambiado algo.
   */
  async function cargarZona(zona: Zona): Promise<void> {
    if (zona === 'cotizar') {
      if (estado.equipos.length === 0) await cargarEquipos();
      return;
    }
    if (zona === 'cotizaciones' && estado.cotizacionesLista.length === 0) {
      await cargarCotizaciones();
      return;
    }
    if (zona === 'rfq' && estado.rfqLista.length === 0 && estado.rfqDetalle === null) {
      await cargarListaRfq();
      return;
    }
    if (zona === 'tarifas' && estado.sheets.length === 0 && estado.alias.length === 0) {
      await cargarTarifas();
      return;
    }
    if (zona === 'proveedores' && estado.dirLista.length === 0) {
      await cargarDirectorio();
      return;
    }
    if (zona === 'administracion') {
      if (estado.adminPestana === 'usuarios' && estado.usuariosLista.length === 0) {
        await cargarUsuarios();
      }
      if (estado.adminPestana === 'margenes' && estado.margenes === null) {
        await cargarMargenes();
      }
      return;
    }
  }

  /** Repinta todo. Cuesta menos que llevar la cuenta de que seccion depende de
   *  que cambio, y es lo que ya hacia el cambio de idioma. */
  function pintarTodo(): void {
    seccionBusqueda.hidden = estado.zona !== 'cotizar';
    pintarCabecera();
    pintarCargaVista();
    pintarMenuZonas();
    pintarResultados();
    pintarCotizar();
    pintarCotizacionesVista();
    pintarRfq();
    pintarTarifasVista();
    pintarProveedoresVista();
    pintarAltas();
    pintarAdministracion();
    pintarUsuariosVista();
    pintarMargenesVista();
    pintarAyuda();
  }

  /**
   * Abre el formulario con la ruta que se acaba de buscar.
   *
   * `criterio` es el RESUELTO de la respuesta: trae el pais, que es lo que
   * convierte "Manzanillo" en un puerto concreto [Invariante 6][H51]. Se
   * propaga tal cual para que la peticion del RFQ no vuelva a ser ambigua.
   */
  function abrirRfq(criterio: RespuestaBusqueda['criterio']): void {
    estado.rfqDetalle = null;
    estado.rfqFaltan = [];
    estado.errorRfq = null;
    estado.rfqForm = {
      pol: criterio.pol.locode ?? criterio.pol.nombre,
      polPais: criterio.pol.pais,
      pod: criterio.pod.locode ?? criterio.pod.nombre,
      podPais: criterio.pod.pais,
      modo: criterio.modo,
      // Ausente en aereo: no hay equipo que pedirle al proveedor.
      equipo: criterio.equipo?.codigo ?? '',
      unidades: 1,
      mercancia: '',
      requisitos: '',
      // En aereo el peso y el volumen SI viajan al RFQ, y son los mismos con
      // los que se busco: es lo que permite al proveedor contestar con un
      // precio de verdad en vez de con una tarifa general.
      pesoKg: criterio.carga?.peso_kg ?? '',
      volumenM3: criterio.carga?.volumen_m3 ?? '',
      temperaturaC: '',
      // La fecha de embarque que se busco es la fecha objetivo del RFQ: es la
      // misma pregunta hecha al proveedor en vez de al catalogo.
      fechaObjetivo: criterio.fecha_embarque,
      nota: '',
      remitente: 'AGS',
      proveedores: [],
      // Se precarga de lo declarado en el buscador y se puede afinar aqui: un
      // RFQ se manda a veces por una carga distinta de la que se buscaba.
      carga: estado.carga,
    };
    irA('rfq');
    void cargarProveedoresRfq();
  }

  async function cargarProveedoresRfq(): Promise<void> {
    const f = estado.rfqForm;
    if (f === null) return;

    estado.rfqCargandoProveedores = true;
    estado.rfqProveedores = [];
    pintarRfq();

    const query = new URLSearchParams({ pol: f.pol, pod: f.pod, modo: f.modo });
    if (f.polPais !== null) query.set('pol_pais', f.polPais);
    if (f.podPais !== null) query.set('pod_pais', f.podPais);

    try {
      const r = await listarProveedoresRfq(query.toString());
      estado.rfqProveedores = r.proveedores;
      // Se marcan los que cubren la ruta Y tienen a donde escribir. La
      // propuesta es de la maquina; quitar o añadir es del operador, y por eso
      // los demas siguen en la lista.
      estado.rfqForm = {
        ...f,
        proveedores: r.proveedores
          .filter((p) => p.precision !== null && p.puede_recibir)
          .map((p) => p.id),
      };
    } catch (error) {
      estado.errorRfq = error instanceof ErrorApi ? error : null;
    } finally {
      estado.rfqCargandoProveedores = false;
      pintarRfq();
    }
  }

  async function crearRfq(f: EstadoFormularioRfq): Promise<void> {
    // Lo que llega es lo que hay escrito, leido del DOM al pulsar Crear. Se
    // guarda para poder repintar con lo escrito si algo falla.
    estado.rfqForm = f;

    const armada = armarSolicitudRfq(f);
    if (!armada.ok) {
      estado.rfqFaltan = armada.faltan;
      estado.errorRfq = null;
      pintarRfq();
      return;
    }

    estado.rfqFaltan = [];
    estado.rfqCreando = true;
    estado.errorRfq = null;
    pintarRfq();

    try {
      estado.rfqDetalle = await crearSolicitudRfq(armada.cuerpo);
      estado.rfqRespuesta = RESPUESTA_VACIA;
      estado.rfqForm = null;
    } catch (error) {
      estado.errorRfq = error instanceof ErrorApi ? error : null;
    } finally {
      estado.rfqCreando = false;
      pintarRfq();
    }
  }

  /** Envuelve una accion sobre la solicitud abierta: mismo manejo de error y
   *  mismo repintado para las cuatro. */
  async function accionSolicitud(
    accion: (id: string) => Promise<DetalleSolicitudRfq>,
  ): Promise<void> {
    const d = estado.rfqDetalle;
    if (d === null) return;

    estado.rfqTrabajando = true;
    estado.errorRfq = null;
    pintarRfq();

    try {
      estado.rfqDetalle = await accion(d.solicitud.id);
    } catch (error) {
      estado.errorRfq = error instanceof ErrorApi ? error : null;
    } finally {
      estado.rfqTrabajando = false;
      pintarRfq();
    }
  }

  async function cargarListaRfq(): Promise<void> {
    estado.rfqCargandoLista = true;
    estado.errorRfq = null;
    pintarRfq();
    try {
      const r = await listarSolicitudesRfq(estado.rfqListaTodas);
      estado.rfqLista = r.solicitudes;
      estado.rfqListaHayMas = r.hay_mas;
    } catch (error) {
      estado.errorRfq = error instanceof ErrorApi ? error : null;
    } finally {
      estado.rfqCargandoLista = false;
      pintarRfq();
    }
  }

  /**
   * Copia al portapapeles, con respaldo.
   *
   * `navigator.clipboard` no existe en todos los contextos --http sin TLS, un
   * iframe sin permiso, jsdom-- y ahi la copia falla en silencio. Cuando no se
   * puede, se selecciona el texto y se dice que hay que pulsar Ctrl+C: una
   * accion que no ocurre y no avisa deja al operador pegando lo que tuviera
   * antes en el portapapeles, que en este caso seria mandarle a un proveedor un
   * correo que no es el suyo.
   */
  function copiar(
    clave: string,
    texto: string,
    respaldo: HTMLTextAreaElement | null,
  ): void {
    const manual = (): void => {
      estado.rfqCopiado = null;
      estado.rfqCopiaManual = clave;
      respaldo?.select();
      pintarRfq();
      // El repintado tira el nodo seleccionado, asi que se vuelve a seleccionar
      // el nuevo. Sin esto el aviso diria "usa Ctrl+C" sin nada seleccionado.
      const nuevo = seccionRfq.querySelector<HTMLTextAreaElement>('.correo-cuerpo');
      nuevo?.select();
    };

    const api = navigator.clipboard as Clipboard | undefined;
    if (api === undefined) {
      manual();
      return;
    }

    api.writeText(texto).then(
      () => {
        estado.rfqCopiado = clave;
        estado.rfqCopiaManual = null;
        pintarRfq();
      },
      () => {
        manual();
      },
    );
  }

  function pintarRfq(): void {
    const c = vaciar(seccionRfq);
    if (estado.zona !== 'rfq') return;

    if (estado.errorRfq !== null) c.appendChild(pintarError(estado.errorRfq, t));

    const detalle = estado.rfqDetalle;
    if (detalle !== null) {
      c.appendChild(
        pintarSolicitud({
          t,
          idioma: estado.idioma,
          detalle,
          copiado: estado.rfqCopiado,
          copiaManual: estado.rfqCopiaManual,
          trabajando: estado.rfqTrabajando,
          respuesta: estado.rfqRespuesta,
          onCopiar: copiar,
          onMarcarEnviada: () => void accionSolicitud(marcarEnviadaRfq),
          onCerrar: () => void accionSolicitud(cerrarSolicitudRfq),
          onCambioRespuesta: (v) => {
            estado.rfqRespuesta = v;
          },
          onRegistrarRespuesta: () => {
            const r = estado.rfqRespuesta;
            if (r.proveedorId === '') return;
            void accionSolicitud(async (id) => {
              const nuevo = await registrarRespuestaRfq(id, {
                proveedor_id: r.proveedorId,
                sin_oferta: r.sinOferta,
                // Quien la registra sale de la sesion, en el servidor.
                ...(r.nota.trim() === '' ? {} : { nota: r.nota.trim() }),
              });
              estado.rfqRespuesta = RESPUESTA_VACIA;
              return nuevo;
            });
          },
          onVolver: () => {
            estado.rfqDetalle = null;
            estado.rfqCopiado = null;
            estado.rfqCopiaManual = null;
            void cargarListaRfq();
          },
        }),
      );
      return;
    }

    const form = estado.rfqForm;
    if (form !== null) {
      c.appendChild(
        pintarFormularioRfq({
          t,
          valores: form,
          equipos: estado.equipos,
          proveedores: estado.rfqProveedores,
          cargandoProveedores: estado.rfqCargandoProveedores,
          creando: estado.rfqCreando,
          faltan: estado.rfqFaltan,
          onCambio: (v) => {
            // Solo llega al cambiar la naturaleza de la carga, que es lo unico
            // que obliga a repintar. Antes llegaba en cada tecla y el panel se
            // reconstruia entero: se escribia una letra y el cursor se iba.
            estado.rfqForm = v;
            pintarRfq();
          },
          onCrear: (v) => void crearRfq(v),
          onCancelar: () => {
            // A la lista de RFQ, que es de donde se sale de un formulario. Antes
            // saltaba al buscador: cancelar en un sitio y aparecer en otro es
            // la forma de que nadie vuelva a pulsar cancelar.
            estado.rfqForm = null;
            pintarRfq();
            void cargarListaRfq();
          },
        }),
      );
      return;
    }

    // El aviso de recorte va ARRIBA de la lista: abajo lo lee quien ya se ha
    // desplazado hasta el final, y quien no llegue concluye que ha visto todo.
    const recorte = avisoRecorte(estado.rfqListaHayMas, estado.rfqLista.length, (n) =>
      t('app.recorte', { n }),
    );
    if (recorte !== null) c.appendChild(recorte);

    c.appendChild(
      pintarListaSolicitudes({
        t,
        idioma: estado.idioma,
        solicitudes: estado.rfqLista,
        todas: estado.rfqListaTodas,
        cargando: estado.rfqCargandoLista,
        onVerTodas: (todas) => {
          estado.rfqListaTodas = todas;
          void cargarListaRfq();
        },
        onAbrir: (id) => {
          void accionSolicitudPorId(id);
        },
      }),
    );
  }

  async function accionSolicitudPorId(id: string): Promise<void> {
    estado.rfqCargandoLista = true;
    pintarRfq();
    try {
      estado.rfqDetalle = await leerSolicitudRfq(id);
      estado.rfqRespuesta = RESPUESTA_VACIA;
    } catch (error) {
      estado.errorRfq = error instanceof ErrorApi ? error : null;
    } finally {
      estado.rfqCargandoLista = false;
      pintarRfq();
    }
  }

  // -------------------------------------------------------------------------
  // Altas de proveedor: la cola del formulario publico
  //
  // Aqui no hay ni un monto --una solicitud de alta no lleva dinero-- asi que
  // `ve_costo` no pinta nada. Lo que si hace falta es el agente elegido: la
  // decision se guarda con quien la tomo, y sin eso la API no la acepta.
  // -------------------------------------------------------------------------

  function pendientes(): number {
    return estado.altasLista.filter((s) => s.estado === 'PENDIENTE').length;
  }

  async function cargarAltas(): Promise<void> {
    estado.altasCargando = true;
    estado.errorAltas = null;
    pintarAltas();
    try {
      const { solicitudes, hay_mas: hayMas } = await listarAltas(estado.altasTodas);
      estado.altasLista = solicitudes;
      estado.altasHayMas = hayMas;
    } catch (error) {
      estado.errorAltas = error instanceof ErrorApi ? error : null;
    } finally {
      estado.altasCargando = false;
      pintarCabecera();
      pintarAltas();
    }
  }

  function abrirAlta(id: string): void {
    const s = estado.altasLista.find((x) => x.id === id) ?? null;
    estado.altaAbierta = s;
    estado.altaCreado = null;
    estado.errorAltas = null;
    estado.altaDecision = s === null ? null : decisionInicial(s);
    pintarAltas();
  }

  async function decidirAlta(aprobar: boolean): Promise<void> {
    const s = estado.altaAbierta;
    const d = estado.altaDecision;
    if (s === null || d === null) return;

    estado.altaTrabajando = true;
    estado.errorAltas = null;
    pintarAltas();

    try {
      const respuesta = aprobar
        ? await aprobarAlta(s.id, {
            // Quien decide sale de la sesion, en el servidor.
            idioma: d.idioma,
            // Enganchar a uno que ya existe, o crearlo con lo que diga el
            // operador. Las dos cosas a la vez no tienen sentido: el nombre y
            // el pais son del tercero que ya esta.
            ...(d.engancharA !== null
              ? { tercero_id: d.engancharA }
              : {
                  nombre: d.nombre,
                  ...(d.pais.trim() === '' ? {} : { pais: d.pais.trim() }),
                }),
            especialidades: d.especialidades
              .filter((e) => e.marcada)
              .map((e) => ({ modo: e.modo, origen: e.origen, destino: e.destino })),
            // El motivo es del rechazo. Al aprobar no se manda: seria un campo
            // que la API tendria que ignorar.
          })
        : await rechazarAlta(s.id, { motivo: d.motivo });

      estado.altaAbierta = respuesta.solicitud;
      estado.altaCreado = respuesta.creado ?? null;
      // La cola se recarga: la fila que se acaba de decidir ya no es pendiente,
      // y el contador de la cabecera es lo que le dice a otro que queda trabajo.
      await cargarAltas();
    } catch (error) {
      estado.errorAltas = error instanceof ErrorApi ? error : null;
    } finally {
      estado.altaTrabajando = false;
      pintarAltas();
    }
  }

  function pintarAltas(): void {
    const c = vaciar(seccionAltas);
    // La cola vive dentro de la zona de Proveedores, en su pestana. Las
    // pestanas las pinta `pintarProveedoresVista`; esto es solo el contenido.
    if (estado.zona !== 'proveedores' || estado.proveedoresPestana !== 'altas') return;

    if (estado.errorAltas !== null) c.appendChild(pintarError(estado.errorAltas, t));

    const abierta = estado.altaAbierta;
    const decision = estado.altaDecision;
    if (abierta !== null && decision !== null) {
      c.appendChild(
        pintarAlta({
          t,
          idioma: estado.idioma,
          solicitud: abierta,
          decision,
          trabajando: estado.altaTrabajando,
          creado: estado.altaCreado,
          onCambio: (d) => {
            estado.altaDecision = d;
            pintarAltas();
          },
          onAprobar: () => void decidirAlta(true),
          onRechazar: () => void decidirAlta(false),
          onVolver: () => {
            estado.altaAbierta = null;
            estado.altaCreado = null;
            pintarAltas();
          },
        }),
      );
      return;
    }

    // El aviso de recorte va ARRIBA de la lista: abajo lo lee quien ya se ha
    // desplazado hasta el final, y quien no llegue concluye que ha visto todo.
    const recorte = avisoRecorte(estado.altasHayMas, estado.altasLista.length, (n) =>
      t('app.recorte', { n }),
    );
    if (recorte !== null) c.appendChild(recorte);

    c.appendChild(
      pintarListaAltas({
        t,
        idioma: estado.idioma,
        solicitudes: estado.altasLista,
        todas: estado.altasTodas,
        cargando: estado.altasCargando,
        onVerTodas: (todas) => {
          estado.altasTodas = todas;
          void cargarAltas();
        },
        onAbrir: abrirAlta,
      }),
    );
  }

  // -------------------------------------------------------------------------
  // Usuarios. Solo ADMIN
  //
  // La pantalla vive aqui como una vista mas y no como una aplicacion aparte:
  // es una tabla y dos formularios. Lo que decide quien puede usarla es la API
  // --403 a quien no es ADMIN-- y no la ausencia del boton.
  // -------------------------------------------------------------------------

  async function cargarUsuarios(): Promise<void> {
    estado.usuariosCargando = true;
    estado.errorUsuarios = null;
    pintarUsuariosVista();
    try {
      const { usuarios } = await listarUsuariosAdmin();
      estado.usuariosLista = usuarios;
    } catch (error) {
      estado.errorUsuarios = error instanceof ErrorApi ? error : null;
    } finally {
      estado.usuariosCargando = false;
      pintarUsuariosVista();
    }
  }

  /** Envuelve una accion sobre un usuario: mismo manejo de error, misma recarga
   *  y mismo repintado para las tres. La lista se recarga entera porque un
   *  cambio mueve columnas que no son la que se toco --las sesiones abiertas de
   *  quien acaba de perderlas-- y refrescar solo la fila las dejaria mintiendo. */
  async function accionUsuario(accion: () => Promise<string | null>): Promise<void> {
    estado.usuariosTrabajando = true;
    estado.errorUsuarios = null;
    estado.usuariosAviso = null;
    pintarUsuariosVista();
    try {
      estado.usuariosAviso = await accion();
      const { usuarios } = await listarUsuariosAdmin();
      estado.usuariosLista = usuarios;
    } catch (error) {
      estado.errorUsuarios = error instanceof ErrorApi ? error : null;
    } finally {
      estado.usuariosTrabajando = false;
      pintarUsuariosVista();
    }
  }

  /**
   * Las pestanas de Administracion.
   *
   * Solo aparecen las que este usuario abre: un SUPERVISOR entra por los
   * margenes y no ve la de usuarios. Con una sola pestana no se pintan --una
   * fila de pestanas con una pestana es un adorno-- y la zona se lee como lo
   * que es.
   */
  function pintarAdministracion(): void {
    const c = vaciar(seccionAdmin);
    if (estado.zona !== 'administracion') return;

    const items = [
      puedo('usuarios.gestionar')
        ? { id: 'usuarios', etiqueta: t('usuarios.titulo') }
        : null,
      puedo('margenes.gestionar')
        ? { id: 'margenes', etiqueta: t('margenes.titulo') }
        : null,
    ].filter((x) => x !== null);

    if (items.length < 2) return;

    c.appendChild(
      pestanas(items, estado.adminPestana, (id) => {
        estado.adminPestana = id as 'usuarios' | 'margenes';
        pintarTodo();
        void cargarZona('administracion');
      }),
    );
  }

  // -------------------------------------------------------------------------
  // Margenes. SUPERVISOR y ADMIN
  //
  // Las reglas NO se editan: se sustituyen. Ver la cabecera de
  // vista/margenes.ts y la de services/api/src/margenes.ts.
  // -------------------------------------------------------------------------

  async function cargarMargenes(): Promise<void> {
    estado.margenesCargando = true;
    estado.errorMargenes = null;
    pintarMargenesVista();
    try {
      estado.margenes = await listarMargenes(estado.margenesTodas);
    } catch (error) {
      estado.errorMargenes = error instanceof ErrorApi ? error : null;
    } finally {
      estado.margenesCargando = false;
      pintarMargenesVista();
    }
  }

  /**
   * Lo que falta antes de mandar. La API valida lo mismo y mejor --conoce los
   * codigos de cargo y los limites-- asi que esto NO es la validacion: es no
   * hacer un viaje de ida y vuelta para decir que falta un campo vacio.
   */
  function faltanEnMargen(f: EstadoFormMargen): string[] {
    const faltan: string[] = [];
    if (f.descripcion.trim() === '') faltan.push('descripcion');
    if (f.que === 'regla' && f.tipo !== 'SIN_MARGEN' && f.valor.trim() === '') {
      faltan.push('valor');
    }
    if (
      f.que === 'piso' &&
      f.margenMinPct.trim() === '' &&
      f.margenMinAbs.trim() === ''
    ) {
      faltan.push('minimo');
    }
    return faltan;
  }

  /** El cuerpo que espera la API. Las dimensiones vacias se mandan como cadena
   *  vacia, que la API lee como "cualquiera" [margenes.ts]. */
  function cuerpoDeMargen(f: EstadoFormMargen): Record<string, unknown> {
    if (f.que === 'piso') {
      return {
        descripcion: f.descripcion.trim(),
        margen_min_pct: f.margenMinPct.trim(),
        margen_min_abs: f.margenMinAbs.trim(),
        modo: f.modo,
        trade: f.trade.trim(),
        cliente_id: f.clienteId,
      };
    }
    return {
      descripcion: f.descripcion.trim(),
      tipo: f.tipo,
      // SIN_MARGEN no lleva valor y la API lo exige igual: se manda cero, que
      // es lo que significa --venta = costo-- y no un hueco.
      valor: f.tipo === 'SIN_MARGEN' ? '0' : f.valor.trim(),
      modo: f.modo,
      tramo: f.tramo,
      codigo_cargo: f.codigoCargo.trim(),
      trade: f.trade.trim(),
      cliente_id: f.clienteId,
      vigencia_desde: f.vigenciaDesde,
      vigencia_hasta: f.vigenciaHasta,
    };
  }

  async function guardarMargen(): Promise<void> {
    const f = estado.margenesForm;
    if (f === null) return;

    const faltan = faltanEnMargen(f);
    if (faltan.length > 0) {
      estado.margenesFaltan = faltan;
      estado.errorMargenes = null;
      pintarMargenesVista();
      return;
    }

    estado.margenesFaltan = [];
    estado.margenesTrabajando = true;
    estado.errorMargenes = null;
    estado.margenesAviso = null;
    pintarMargenesVista();

    const cuerpo = cuerpoDeMargen(f);
    try {
      if (f.que === 'regla') {
        if (f.id === null) {
          const { regla } = await crearReglaMargen(cuerpo);
          estado.margenesAviso = t('margenes.aviso.creada', { d: regla.descripcion });
        } else {
          const { nueva } = await sustituirReglaMargen(f.id, cuerpo);
          // Se dice lo que paso de verdad: nacio una regla nueva y la anterior
          // quedo retirada. "Guardado" escondería la mitad.
          estado.margenesAviso = t('margenes.aviso.sustituida', { d: nueva.descripcion });
        }
      } else {
        if (f.id === null) {
          const { piso } = await crearPisoMargen(cuerpo);
          estado.margenesAviso = t('margenes.aviso.creado', { d: piso.descripcion });
        } else {
          const { nueva } = await sustituirPisoMargen(f.id, cuerpo);
          estado.margenesAviso = t('margenes.aviso.sustituido', { d: nueva.descripcion });
        }
      }
      estado.margenesForm = null;
      await cargarMargenes();
    } catch (error) {
      estado.errorMargenes = error instanceof ErrorApi ? error : null;
    } finally {
      estado.margenesTrabajando = false;
      pintarMargenesVista();
    }
  }

  async function cambiarEstadoMargen(
    que: 'regla' | 'piso',
    id: string,
    activa: boolean,
  ): Promise<void> {
    estado.margenesTrabajando = true;
    estado.errorMargenes = null;
    estado.margenesAviso = null;
    pintarMargenesVista();
    try {
      const r =
        que === 'regla'
          ? (await cambiarEstadoReglaMargen(id, activa)).regla
          : (await cambiarEstadoPisoMargen(id, activa)).piso;
      estado.margenesAviso = t(
        activa ? 'margenes.aviso.activada' : 'margenes.aviso.desactivada',
        {
          d: r.descripcion,
        },
      );
      await cargarMargenes();
    } catch (error) {
      estado.errorMargenes = error instanceof ErrorApi ? error : null;
    } finally {
      estado.margenesTrabajando = false;
      pintarMargenesVista();
    }
  }

  function pintarMargenesVista(): void {
    const c = vaciar(seccionMargenes);
    if (estado.zona !== 'administracion' || estado.adminPestana !== 'margenes') return;
    if (!puedo('margenes.gestionar')) return;

    if (estado.errorMargenes !== null)
      c.appendChild(pintarError(estado.errorMargenes, t));

    c.appendChild(
      pintarMargenes({
        t,
        idioma: estado.idioma,
        datos: estado.margenes,
        clientes: estado.clientes,
        cargando: estado.margenesCargando,
        trabajando: estado.margenesTrabajando,
        todas: estado.margenesTodas,
        form: estado.margenesForm,
        faltan: estado.margenesFaltan,
        aviso: estado.margenesAviso,
        onVerTodas: (todas) => {
          estado.margenesTodas = todas;
          void cargarMargenes();
        },
        onAbrirForm: (form) => {
          estado.margenesForm = form;
          estado.margenesFaltan = [];
          estado.margenesAviso = null;
          pintarMargenesVista();
        },
        onCambioForm: (form) => {
          // No se repinta: los controles ya llevan lo que se escribio, y
          // repintar en cada tecla tiraria el foco del campo.
          estado.margenesForm = form;
        },
        onGuardar: () => void guardarMargen(),
        onEstado: (que, id, activa) => {
          void cambiarEstadoMargen(que, id, activa);
        },
      }),
    );
  }

  /**
   * Las contrasenas generadas, o el formulario del lote, o la lista.
   *
   * El orden importa: las credenciales tapan todo lo demas mientras esten en
   * pantalla. Se ven una vez, y una lista de usuarios debajo invita a mirar
   * hacia otro lado antes de copiarlas.
   */
  /**
   * Lo que falta antes de mandar el lote. La API valida lo mismo; esto evita el
   * viaje y, sobre todo, marca QUE fila --con diez, "falta el nombre" a secas
   * obliga a revisarlas todas--.
   *
   * Las filas del todo vacias no cuentan como huecos: son las que se anadieron
   * de mas y se ignoran al mandar.
   */
  function faltanEnLote(filas: readonly FilaLote[]): number[] {
    return filas
      .map((f, i) => ({ f, i }))
      .filter(({ f }) => !filaVacia(f))
      .filter(
        ({ f }) => f.nombre.trim() === '' || f.email.trim() === '' || f.veCosto === null,
      )
      .map(({ i }) => i);
  }

  const filaVacia = (f: FilaLote): boolean =>
    f.nombre.trim() === '' && f.email.trim() === '';

  async function crearLote(filas: readonly FilaLote[]): Promise<void> {
    // Lo que llega es lo que hay escrito, no lo que guardaba el estado: los
    // campos se leen del DOM al mandar [vista/lote-usuarios.ts]. Se guarda para
    // poder repintar el formulario con lo escrito si algo falla.
    estado.usuariosLote = filas;

    const faltan = faltanEnLote(filas);
    const utiles = filas.filter((f) => !filaVacia(f));

    if (faltan.length > 0 || utiles.length === 0) {
      estado.usuariosLoteFaltan = faltan;
      estado.errorUsuarios = null;
      // Sin ninguna fila util no hay huecos que marcar y tampoco hay nada que
      // crear: se marca la primera para que el aviso tenga a que apuntar.
      if (utiles.length === 0) estado.usuariosLoteFaltan = [0];
      pintarUsuariosVista();
      return;
    }

    estado.usuariosLoteFaltan = [];
    estado.usuariosTrabajando = true;
    estado.errorUsuarios = null;
    estado.usuariosAviso = null;
    pintarUsuariosVista();

    try {
      const { creados } = await crearUsuariosEnLote(
        utiles.map((f) => ({
          nombre: f.nombre.trim(),
          email: f.email.trim(),
          rol: f.rol,
          ve_costo: f.veCosto,
          // Sin `contrasena`: las genera el servidor. Ver lote-usuarios.ts.
        })),
      );
      estado.usuariosCredenciales = creados;
      estado.usuariosCopiado = false;
      estado.usuariosCopiaManual = false;
      // El formulario se tira solo cuando el lote entro entero. Si fallo, sigue
      // puesto con lo escrito: la API no creo ninguno y lo unico que hay que
      // hacer es corregir la fila que lo dice.
      estado.usuariosLote = null;
      const { usuarios } = await listarUsuariosAdmin();
      estado.usuariosLista = usuarios;
    } catch (error) {
      estado.errorUsuarios = error instanceof ErrorApi ? error : null;
    } finally {
      estado.usuariosTrabajando = false;
      pintarUsuariosVista();
    }
  }

  /**
   * Copia las credenciales, con respaldo.
   *
   * Mismo criterio que el correo del RFQ: `navigator.clipboard` no existe en
   * todos los contextos, y ahi la copia falla en silencio. Aqui el silencio
   * seria peor todavia --estas contrasenas no se pueden volver a leer-- asi
   * que cuando no se puede se selecciona el texto y se dice que hay que pulsar
   * Ctrl+C.
   */
  function copiarCredenciales(texto: string): void {
    const manual = (): void => {
      estado.usuariosCopiado = false;
      estado.usuariosCopiaManual = true;
      pintarUsuariosVista();
      seccionUsuarios.querySelector<HTMLTextAreaElement>('.credenciales-texto')?.select();
    };

    const api = navigator.clipboard as Clipboard | undefined;
    if (api === undefined) {
      manual();
      return;
    }
    api.writeText(texto).then(
      () => {
        estado.usuariosCopiado = true;
        estado.usuariosCopiaManual = false;
        pintarUsuariosVista();
      },
      () => {
        manual();
      },
    );
  }

  function pintarUsuariosVista(): void {
    const c = vaciar(seccionUsuarios);
    if (estado.zona !== 'administracion' || estado.adminPestana !== 'usuarios') return;
    if (!puedo('usuarios.gestionar')) return;

    if (estado.errorUsuarios !== null) {
      c.appendChild(pintarError(estado.errorUsuarios, t));
    }

    const credenciales = estado.usuariosCredenciales;
    if (credenciales !== null) {
      c.appendChild(
        pintarCredenciales({
          t,
          creados: credenciales,
          copiado: estado.usuariosCopiado,
          copiaManual: estado.usuariosCopiaManual,
          onCopiar: (texto) => {
            copiarCredenciales(texto);
          },
          onCerrar: () => {
            estado.usuariosCredenciales = null;
            estado.usuariosCopiado = false;
            estado.usuariosCopiaManual = false;
            pintarUsuariosVista();
          },
        }),
      );
      return;
    }

    const lote = estado.usuariosLote;
    if (lote !== null) {
      c.appendChild(
        pintarLoteUsuarios({
          t,
          filas: lote,
          trabajando: estado.usuariosTrabajando,
          faltan: estado.usuariosLoteFaltan,
          onCambio: (filas) => {
            // Solo llega al anadir o quitar una fila, nunca al teclear: la
            // tabla no se repinta entre teclas [vista/lote-usuarios.ts]. Y lo
            // que llega es lo que hay escrito en ese momento, leido del DOM.
            estado.usuariosLote = filas;
            pintarUsuariosVista();
          },
          onCrear: (filas) => void crearLote(filas),
          onCancelar: () => {
            estado.usuariosLote = null;
            estado.usuariosLoteFaltan = [];
            pintarUsuariosVista();
          },
        }),
      );
      return;
    }

    c.appendChild(
      pintarUsuarios({
        t,
        idioma: estado.idioma,
        usuarios: estado.usuariosLista,
        cargando: estado.usuariosCargando,
        trabajando: estado.usuariosTrabajando,
        yo: yo().id,
        alta: estado.usuariosAlta,
        reseteando: estado.usuariosReseteo,
        aviso: estado.usuariosAviso,
        onAbrirAlta: (abierta) => {
          estado.usuariosAlta = abierta ? ALTA_VACIA : null;
          estado.usuariosAviso = null;
          pintarUsuariosVista();
        },
        onAbrirLote: () => {
          estado.usuariosLote = LOTE_INICIAL;
          estado.usuariosLoteFaltan = [];
          estado.usuariosAlta = null;
          estado.usuariosAviso = null;
          pintarUsuariosVista();
        },
        onCambioAlta: (v) => {
          // No se repinta: los controles ya llevan lo que se escribio, y
          // repintar en cada tecla tiraria el foco del campo.
          estado.usuariosAlta = v;
        },
        onCrear: () => {
          const a = estado.usuariosAlta;
          if (a === null || a.veCosto === null) return;
          void accionUsuario(async () => {
            const creado = await crearUsuarioAdmin({
              nombre: a.nombre.trim(),
              email: a.email.trim(),
              rol: a.rol,
              ve_costo: a.veCosto,
              // La contrasena NO se recorta: un espacio al final es parte de
              // ella, y quitarlo aqui la cambiaria sin decirlo. Vacia significa
              // "generala tu", y entonces no se manda el campo.
              ...(a.contrasena === '' ? {} : { contrasena: a.contrasena }),
            });
            estado.usuariosAlta = null;
            if (creado.contrasena_temporal !== null) {
              // Generada: hay que enseñarla, y solo se puede una vez.
              estado.usuariosCredenciales = [creado];
              return null;
            }
            return t('usuarios.aviso.creado', { nombre: creado.usuario.nombre });
          });
        },
        onEstado: (id, activo) => {
          void accionUsuario(async () => {
            const { usuario } = await cambiarEstadoUsuario(id, activo);
            return t(activo ? 'usuarios.aviso.reactivado' : 'usuarios.aviso.baja', {
              nombre: usuario.nombre,
            });
          });
        },
        onAbrirReseteo: (id) => {
          estado.usuariosReseteo = id === null ? null : { id, contrasena: '' };
          estado.usuariosAviso = null;
          pintarUsuariosVista();
        },
        onCambioReseteo: (contrasena) => {
          const r = estado.usuariosReseteo;
          if (r !== null) estado.usuariosReseteo = { ...r, contrasena };
        },
        onResetear: () => {
          const r = estado.usuariosReseteo;
          if (r === null) return;
          void accionUsuario(async () => {
            const hecho = await resetearContrasenaUsuario(r.id, r.contrasena);
            estado.usuariosReseteo = null;
            if (hecho.contrasena_temporal !== null) {
              estado.usuariosCredenciales = [hecho];
              return null;
            }
            // Cuantas sesiones se cerraron: es la confirmacion de que el
            // reseteo surtio efecto ahora y no cuando caduque algo.
            return t('usuarios.aviso.reseteada', {
              nombre: hecho.usuario.nombre,
              n: String(hecho.sesiones_cerradas),
            });
          });
        },
      }),
    );
  }

  // -------------------------------------------------------------------------
  // Cotizaciones emitidas
  //
  // La zona que no existia. Aqui NO se calcula nada: los montos y el veredicto
  // de aprobacion llegan congelados [Invariante 5][migracion 0030]. Ver la
  // cabecera de vista/cotizaciones.ts.
  // -------------------------------------------------------------------------

  async function cargarCotizaciones(): Promise<void> {
    estado.cotizacionesCargando = true;
    estado.errorCotizaciones = null;
    pintarCotizacionesVista();
    try {
      const r = await listarCotizaciones();
      estado.cotizacionesLista = r.cotizaciones;
      estado.cotizacionesHayMas = r.hay_mas;
      estado.cotizacionesSoloMias = r.solo_mias;
    } catch (error) {
      estado.errorCotizaciones = error instanceof ErrorApi ? error : null;
    } finally {
      estado.cotizacionesCargando = false;
      pintarCotizacionesVista();
    }
  }

  async function abrirCotizacion(id: string): Promise<void> {
    estado.cotizacionesCargando = true;
    estado.errorCotizaciones = null;
    pintarCotizacionesVista();
    try {
      estado.cotizacionAbierta = await leerCotizacionGuardada(id);
    } catch (error) {
      estado.errorCotizaciones = error instanceof ErrorApi ? error : null;
    } finally {
      estado.cotizacionesCargando = false;
      pintarCotizacionesVista();
    }
  }

  function pintarCotizacionesVista(): void {
    const c = vaciar(seccionCotizaciones);
    if (estado.zona !== 'cotizaciones') return;

    if (estado.errorCotizaciones !== null) {
      c.appendChild(pintarError(estado.errorCotizaciones, t));
    }

    const abierta = estado.cotizacionAbierta;
    if (abierta !== null) {
      c.appendChild(
        pintarDetalleCotizacion({
          t,
          idioma: estado.idioma,
          detalle: abierta,
          onVolver: () => {
            estado.cotizacionAbierta = null;
            pintarCotizacionesVista();
          },
        }),
      );
      return;
    }

    // El aviso de recorte va ARRIBA de la lista: abajo lo lee quien ya se ha
    // desplazado hasta el final, y quien no llegue concluye que ha visto todo.
    const recorte = avisoRecorte(
      estado.cotizacionesHayMas,
      estado.cotizacionesLista.length,
      (n) => t('app.recorte', { n }),
    );
    if (recorte !== null) c.appendChild(recorte);

    c.appendChild(
      pintarListaCotizaciones({
        t,
        idioma: estado.idioma,
        cotizaciones: estado.cotizacionesLista,
        soloMias: estado.cotizacionesSoloMias,
        cargando: estado.cotizacionesCargando,
        onAbrir: (id) => {
          void abrirCotizacion(id);
        },
      }),
    );
  }

  // -------------------------------------------------------------------------
  // Tarifas: las dos colas del catalogo
  //
  // Firmar un sheet escribe; confirmar un alias no se hace desde aqui y la
  // pantalla dice por que. Ver la cabecera de vista/tarifas.ts.
  // -------------------------------------------------------------------------

  async function cargarTarifas(): Promise<void> {
    estado.tarifasCargando = true;
    estado.errorTarifas = null;
    pintarTarifasVista();
    try {
      // Las dos a la vez: son dos colas independientes y encadenarlas solo
      // haria esperar el doble.
      const [sheets, alias] = await Promise.all([listarColaSheets(), listarColaAlias()]);
      estado.sheets = sheets.sheets;
      estado.alias = alias.alias;
    } catch (error) {
      estado.errorTarifas = error instanceof ErrorApi ? error : null;
    } finally {
      estado.tarifasCargando = false;
      // El menu tambien: su contador sale de esta lista.
      pintarMenuZonas();
      pintarTarifasVista();
    }
  }

  async function firmarSheet(id: string): Promise<void> {
    estado.tarifasFirmando = id;
    estado.tarifasAviso = null;
    estado.errorTarifas = null;
    pintarTarifasVista();
    try {
      const firma = await verificarSheet(id);
      // Cuantas tarifas quedaron cotizables: es lo que convierte "firmado" en
      // "esto ya sirve para algo", y tambien lo que delata una firma sobre un
      // documento con todas las tarifas vencidas.
      estado.tarifasAviso = t('tarifas.firmado', {
        n: String(firma.records_cotizables),
      });
      await cargarTarifas();
    } catch (error) {
      estado.errorTarifas = error instanceof ErrorApi ? error : null;
    } finally {
      estado.tarifasFirmando = null;
      pintarTarifasVista();
    }
  }

  function pintarTarifasVista(): void {
    const c = vaciar(seccionTarifas);
    if (estado.zona !== 'tarifas') return;

    if (estado.errorTarifas !== null) c.appendChild(pintarError(estado.errorTarifas, t));

    c.appendChild(
      pintarTarifas({
        t,
        idioma: estado.idioma,
        sheets: estado.sheets,
        alias: estado.alias,
        cargando: estado.tarifasCargando,
        firmando: estado.tarifasFirmando,
        aviso: estado.tarifasAviso,
        onFirmar: (id) => {
          void firmarSheet(id);
        },
      }),
    );
  }

  // -------------------------------------------------------------------------
  // Proveedores: el directorio y la cola de altas
  //
  // Dos trabajos en una zona y por eso dos pestanas: quien esta dentro de la
  // agenda, y quien ha pedido entrar. La cola lleva su numero encima porque el
  // formulario publico no avisa a nadie.
  // -------------------------------------------------------------------------

  async function cargarDirectorio(): Promise<void> {
    estado.dirCargando = true;
    estado.errorDirectorio = null;
    pintarProveedoresVista();
    try {
      const r = await listarDirectorio(estado.dirQ);
      estado.dirLista = r.proveedores;
      estado.dirHayMas = r.hay_mas;
    } catch (error) {
      estado.errorDirectorio = error instanceof ErrorApi ? error : null;
    } finally {
      estado.dirCargando = false;
      pintarProveedoresVista();
    }
  }

  function pintarProveedoresVista(): void {
    const c = vaciar(seccionProveedores);
    if (estado.zona !== 'proveedores') return;

    c.appendChild(
      pestanas(
        [
          { id: 'directorio', etiqueta: t('proveedores.pestana.directorio') },
          {
            id: 'altas',
            etiqueta: t('proveedores.pestana.altas'),
            pendientes: pendientes(),
          },
        ],
        estado.proveedoresPestana,
        (id) => {
          estado.proveedoresPestana = id as 'directorio' | 'altas';
          estado.altaAbierta = null;
          estado.altaCreado = null;
          pintarProveedoresVista();
          pintarAltas();
          if (id === 'directorio' && estado.dirLista.length === 0)
            void cargarDirectorio();
          if (id === 'altas') void cargarAltas();
        },
      ),
    );

    if (estado.proveedoresPestana === 'altas') return;

    if (estado.errorDirectorio !== null) {
      c.appendChild(pintarError(estado.errorDirectorio, t));
    }

    // El aviso de recorte va ARRIBA de la lista: abajo lo lee quien ya se ha
    // desplazado hasta el final, y quien no llegue concluye que ha visto todo.
    const recorte = avisoRecorte(estado.dirHayMas, estado.dirLista.length, (n) =>
      t('app.recorte', { n }),
    );
    if (recorte !== null) c.appendChild(recorte);

    c.appendChild(
      pintarDirectorio({
        t,
        proveedores: estado.dirLista,
        q: estado.dirQ,
        hayMas: estado.dirHayMas,
        cargando: estado.dirCargando,
        abierto: estado.dirAbierto,
        onBuscar: (q) => {
          estado.dirQ = q;
          void cargarDirectorio();
        },
        onAbrir: (id) => {
          estado.dirAbierto = id;
          pintarProveedoresVista();
        },
        onIrAAltas: () => {
          estado.proveedoresPestana = 'altas';
          pintarProveedoresVista();
          pintarAltas();
          void cargarAltas();
        },
      }),
    );
  }

  // -------------------------------------------------------------------------
  // Ayuda
  //
  // El manual, organizado por TAREAS y no por pantallas: buscar, leer los
  // resultados, el peso que se cobra, cotizar, y que hacer cuando no sale nada.
  // El indice de zonas se queda dentro como una seccion mas.
  //
  // El contenido vive en vista/ayuda.ts. Lo que hay que leer de ahi antes de
  // tocarlo es por que NO redacta las banderas ni las causas con sus propias
  // palabras: las lee de las mismas claves que la pantalla.
  // -------------------------------------------------------------------------

  function pintarAyuda(): void {
    const c = vaciar(seccionAyuda);
    if (estado.zona !== 'ayuda') return;

    c.appendChild(
      pintarManual({
        t,
        capacidades: yo().capacidades,
        onIrA: (zona) => {
          irA(zona);
        },
      }),
    );
  }

  /**
   * Pestanas dentro de una zona.
   *
   * Son zonas que llevan dos trabajos dentro --el directorio y su cola, los
   * usuarios y los margenes-- y partirlas en dos entradas de menu haria un menu
   * de ocho lineas donde dos son la misma cosa vista de dos maneras.
   */
  function pestanas(
    items: readonly { id: string; etiqueta: string; pendientes?: number }[],
    activa: string,
    onElegir: (id: string) => void,
  ): HTMLElement {
    return el(
      'div',
      { clase: 'pestanas', attrs: { role: 'tablist' } },
      items.map((i) =>
        el('button', {
          clase: `pestana ${i.id === activa ? 'pestana-activa' : ''}`,
          texto:
            i.pendientes === undefined || i.pendientes === 0
              ? i.etiqueta
              : `${i.etiqueta} (${String(i.pendientes)})`,
          attrs: {
            type: 'button',
            role: 'tab',
            'aria-selected': i.id === activa ? 'true' : 'false',
          },
          onClick: () => {
            onElegir(i.id);
          },
        }),
      ),
    );
  }

  // -------------------------------------------------------------------------

  /**
   * Salir.
   *
   * Se llama a la API primero y se vuelve al login pase lo que pase. Un
   * `DELETE /sesion` que falla por red deja la sesion viva en el servidor, y
   * quedarse dentro por eso seria lo contrario de lo que se pidio; el navegador
   * vuelve al login y la sesion caduca sola. Lo que NO se hace es solo borrar
   * la cookie: eso no cierra nada, y la cookie es HttpOnly, asi que esta
   * pantalla no podria borrarla aunque quisiera.
   */
  async function salir(): Promise<void> {
    try {
      await cerrarSesion();
    } catch {
      // Sin ruido: se sale igual.
    }
    montarLogin(raiz);
  }

  // Si la sesion muere mientras se trabaja --caduco, la cerraron en otra
  // pestaña, un ADMIN dio de baja o reseteo-- la siguiente peticion devuelve
  // 401 y la pantalla vuelve a la puerta. Sin esto, cada accion fallaria con un
  // error distinto y pareceria rota en vez de cerrada.
  alPerderSesion(() => {
    montarLogin(raiz, true);
  });

  /**
   * La vuelta atras del navegador, y las URLs pegadas a mano.
   *
   * `hashchange` cubre las dos: el boton de atras y alguien que escribe
   * `#/tarifas` en la barra. Se pasa `desdeUrl` para no volver a escribir la
   * URL que ya es la buena --eso anadiria una entrada al historial y el boton
   * de atras dejaria de salir de la zona--.
   *
   * Una ruta que no existe no cambia de zona y no se corrige en silencio: se
   * deja donde estaba. Redirigir al buscador haria que un enlace roto y la
   * portada se vieran igual.
   */
  window.addEventListener('hashchange', () => {
    const zona = zonaDeRuta(window.location.hash);
    if (zona !== null) {
      estado.rutaRota = null;
      if (zona !== estado.zona) irA(zona, true);
      else pintarMenuZonas();
      return;
    }

    // NO SE CAMBIA DE ZONA, PERO SE DICE.
    //
    // La regla de arriba sigue: una ruta que no existe no redirige al buscador,
    // porque entonces un enlace roto y la portada se verian igual. Lo que
    // faltaba era la otra mitad: sin avisar, el resultado era que no pasaba
    // NADA --la barra de direcciones decia `#/administracin` y la pantalla
    // seguia en Cotizaciones-- y un enlace roto se veia igual que un enlace que
    // no se pulso. Se vio recorriendo la aplicacion.
    //
    // Se guarda la ruta tal como se escribio, porque el error util es "eso que
    // escribiste no existe" y no "algo fallo".
    estado.rutaRota = window.location.hash;
    pintarMenuZonas();
  });

  document.documentElement.lang = estado.idioma;
  // La URL se pone ya, aunque se haya entrado sin fragmento: sin esto, la
  // primera vuelta atras del navegador saldria de la aplicacion entera en vez
  // de no hacer nada.
  if (zonaDeRuta(window.location.hash) === null) {
    window.location.replace(
      `${window.location.pathname}${window.location.search}${rutaDeZona(estado.zona)}`,
    );
  }
  document.title = `${t(`zona.${estado.zona}.nombre`)} · ${t('app.nombre')}`;
  montarFormulario();
  // El estado inicial se pinta ya, sin esperar a la primera busqueda. Antes
  // esta seccion arrancaba vacia y solo se llenaba al ocurrir algo, asi que la
  // pantalla recien abierta era indistinguible de una busqueda que no devolvio
  // nada y de una que fallo en silencio.
  pintarTodo();
  void cargarCatalogos();
}

// ---------------------------------------------------------------------------
// La puerta
// ---------------------------------------------------------------------------

/**
 * Monta el login y no vuelve.
 *
 * `caducada` distingue "todavia no has entrado" de "estabas dentro y se acabo".
 * Son la misma pantalla y no el mismo mensaje: en el segundo caso alguien
 * estaba a mitad de algo y merece saber por que se le fue.
 */
function montarLogin(raiz: HTMLElement, caducada = false): void {
  // Deja de escuchar el que registro la aplicacion anterior: si no, un 401 de
  // una peticion en vuelo remontaria el login encima del que ya esta puesto y
  // borraria lo que se estuviera escribiendo.
  alPerderSesion(() => {
    /* ya estamos en la puerta */
  });

  document.body.classList.add('pantalla-login');

  let idioma = idiomaGuardado();
  let t = crearTraductor(idioma);
  let valores: ValoresLogin = { email: '', contrasena: '' };
  let entrando = false;
  let error: ErrorApi | null = null;
  let primera = true;

  function pintar(): void {
    document.documentElement.lang = idioma;
    vaciar(raiz).appendChild(
      pintarLogin({
        t,
        idioma,
        valores,
        entrando,
        error,
        onIdioma: (i) => {
          idioma = i;
          guardarIdioma(i);
          t = crearTraductor(i);
          pintar();
        },
        onEntrar: (v) => {
          void entrar(v);
        },
      }),
    );
    // El cursor solo se mueve al abrir la puerta y despues de un fallo. Al
    // cambiar de idioma no: llevarselo del selector que se acaba de usar es
    // desconcertante.
    if (primera || error !== null) enfocarLogin(raiz, error !== null);
    primera = false;
  }

  async function entrar(v: ValoresLogin): Promise<void> {
    // El correo se conserva y la contrasena NO: es lo que hay que volver a
    // escribir, y dejarla puesta en un campo despues de un fallo invita a
    // pulsar otra vez lo mismo.
    valores = { email: v.email, contrasena: '' };
    entrando = true;
    error = null;
    pintar();

    try {
      const sesion = await iniciarSesion(v.email, v.contrasena);
      // Si la contrasena se la dieron, la siguiente pantalla es cambiarla y no
      // la aplicacion: con la bandera puesta, la API contesta 409 a todo lo
      // demas [migracion 0032].
      if (sesion.usuario.debe_cambiar_contrasena) {
        vaciar(raiz);
        montarCambio(raiz, sesion);
        return;
      }
      document.body.classList.remove('pantalla-login');
      vaciar(raiz);
      montarApp(raiz, sesion);
    } catch (causa) {
      // El mensaje lo pone la API y es UNO SOLO para las cuatro formas de
      // fallar. Esta pantalla no lo interpreta ni lo completa: hacerlo la
      // convertiria en un comprobador de quien trabaja aqui.
      error =
        causa instanceof ErrorApi
          ? causa
          : new ErrorApi(0, 'ERROR_INTERNO', String(causa));
      entrando = false;
      pintar();
    }
  }

  pintar();

  if (caducada) {
    // Se pinta despues del primer `pintar()` y no como parte del formulario:
    // no es un error de lo que se acaba de escribir, es lo que paso antes.
    raiz
      .querySelector('.login-panel')
      ?.insertBefore(
        el('p', { clase: 'aviso aviso-neutro', texto: t('login.caducada') }),
        raiz.querySelector('.login-form'),
      );
  }
}

/**
 * La puerta de dentro: cambiar la contrasena antes de llegar a nada.
 *
 * No es una pantalla que esconde el resto. Mientras `debe_cambiar_contrasena`
 * sea true la API contesta 409 a todo lo demas [migracion 0032], asi que
 * montar la aplicacion pintaria siete zonas que devuelven un error. Ver la
 * cabecera de vista/contrasena.ts.
 */
function montarCambio(raiz: HTMLElement, sesion: Sesion): void {
  // Igual que el login: se deja de escuchar el que registrara una aplicacion
  // anterior, para que un 401 en vuelo no monte el login encima de esto.
  alPerderSesion(() => {
    /* aqui todavia no se trabaja */
  });

  document.body.classList.add('pantalla-login');

  let idioma = idiomaGuardado();
  let t = crearTraductor(idioma);
  let valores: ValoresCambio = CAMBIO_VACIO;
  let trabajando = false;
  let error: ErrorApi | null = null;
  let aviso: string | null = null;
  let primera = true;

  function pintar(): void {
    document.documentElement.lang = idioma;
    document.title = `${t('cambio.titulo')} · ${t('app.nombre')}`;
    vaciar(raiz).appendChild(
      pintarCambioObligado({
        t,
        idioma,
        nombre: sesion.usuario.nombre,
        valores,
        minimo: MIN_CONTRASENA,
        trabajando,
        error,
        aviso,
        onIdioma: (i) => {
          idioma = i;
          guardarIdioma(i);
          t = crearTraductor(i);
          pintar();
        },
        onGuardar: (v) => void guardar(v),
        onSalir: () => void salirDesdeCambio(),
      }),
    );
    if (primera) enfocarCambio(raiz);
    primera = false;
  }

  async function guardar(v: ValoresCambio): Promise<void> {
    // Se conservan para repintar el formulario con lo que ya estaba escrito si
    // algo falla. Los campos se leen del DOM al mandar, no al teclear: ver la
    // cabecera de vista/contrasena.ts.
    valores = v;

    // Lo que se puede comprobar aqui se comprueba aqui: mandar dos contrasenas
    // distintas al servidor para que conteste lo que la pantalla ya sabia es un
    // viaje de ida y vuelta y medio segundo de bcrypt.
    if (valores.nueva !== valores.repetida) {
      aviso = t('cambio.noCoinciden');
      error = null;
      pintar();
      return;
    }
    if (valores.nueva.length < MIN_CONTRASENA) {
      aviso = t('cambio.corta', { n: String(MIN_CONTRASENA) });
      error = null;
      pintar();
      return;
    }

    aviso = null;
    error = null;
    trabajando = true;
    pintar();

    try {
      await cambiarContrasenaPropia(valores.actual, valores.nueva);
      // Se vuelve a preguntar quien soy en vez de dar por hecho que la bandera
      // bajo: la sesion la resuelve el servidor y esta pantalla no la inventa.
      const nueva = await recuperarSesion();
      document.body.classList.remove('pantalla-login');
      vaciar(raiz);
      if (nueva === null) montarLogin(raiz, true);
      else montarApp(raiz, nueva);
    } catch (causa) {
      error =
        causa instanceof ErrorApi
          ? causa
          : new ErrorApi(0, 'ERROR_INTERNO', String(causa));
      // La actual se borra y las nuevas se conservan: lo que fallo casi siempre
      // es la que le dictaron, y volver a escribir las otras dos es castigo.
      valores = { ...valores, actual: '' };
      trabajando = false;
      primera = true;
      pintar();
    }
  }

  async function salirDesdeCambio(): Promise<void> {
    try {
      await cerrarSesion();
    } catch {
      // Sin ruido: se sale igual.
    }
    vaciar(raiz);
    montarLogin(raiz);
  }

  pintar();
}

/**
 * Punto de entrada.
 *
 * Pregunta quien soy antes de pintar nada. Un 401 no es un fallo: es la
 * respuesta esperada de quien todavia no ha entrado, y lleva al login. Un
 * error de verdad --la API caida-- se dice, porque pintar un login que no va a
 * funcionar manda a depurar la contrasena en vez del servicio.
 */
export function arrancar(raiz: HTMLElement): void {
  void (async () => {
    let sesion;
    try {
      sesion = await recuperarSesion();
    } catch (error) {
      vaciar(raiz).appendChild(
        pintarError(
          error instanceof ErrorApi ? error : new ErrorApi(0, 'SIN_RED', String(error)),
          crearTraductor(idiomaGuardado()),
        ),
      );
      return;
    }

    if (sesion === null) montarLogin(raiz);
    else if (sesion.usuario.debe_cambiar_contrasena) montarCambio(raiz, sesion);
    else montarApp(raiz, sesion);
  })();
}

/**
 * Un error de la API.
 *
 * El titulo sale del CODIGO y el mensaje del servidor se pinta debajo. Es la
 * regla de errores.ts: ramificar sobre el texto se rompe en cuanto se corrige
 * una tilde, y esta pantalla es bilingue mientras que los mensajes de la API no
 * lo son.
 */
function pintarError(error: ErrorApi, t: Traductor): HTMLElement {
  const detalle = error.detalle;
  return el('div', { clase: 'panel aviso aviso-error' }, [
    el('strong', {
      texto: error.codigo === 'SIN_RED' ? t('error.red') : t(`error.${error.codigo}`),
    }),
    el('p', { texto: error.message }),
    // El `motivo` de TARIFA_NO_COTIZABLE: las cuatro razones se arreglan de
    // forma distinta y un mensaje generico manda al agente a adivinar cual es.
    typeof detalle?.motivo === 'string'
      ? el('p', { clase: 'tenue', texto: `motivo: ${detalle.motivo}` })
      : null,
  ]);
}
